import { useCallback, useEffect } from "react"

import {
  Transcript,
  TranscriptAlternative,
  TranscriptResult,
} from "amazon-chime-sdk-js"

import useFlushingBuffer from "./useFlushingBuffer"

type TranscriptBuffer = {
  text: string
  speakerId: string
  startTime: Date
  piiEntities: NonNullable<TranscriptAlternative["entities"]>
  vocabularyFilterMatches: string[]
}

const useTranscriptForwarding = ({ enabled, audioVideo, userId, channel }) => {
  const handleTextChunk = useCallback(
    (buffer: TranscriptBuffer) => {
      if (!channel) return
      const trimmedText = buffer.text.trim()
      if (trimmedText.length > 1) {
        channel.perform("transcript_chunk", {
          text: trimmedText,
          speakerId: buffer.speakerId,
          timestamp: buffer.startTime,
          piiEntities: buffer.piiEntities,
          vocabularyFilterMatches: buffer.vocabularyFilterMatches,
        })
      }
    },
    [channel]
  )

  const { bufferRef, flush, scheduleFlush } =
    useFlushingBuffer<TranscriptBuffer>({ handleTextChunk })

  useEffect(() => {
    if (!enabled) return
    if (!audioVideo || !channel) return // cloudroom not ready

    const handleTranscriptEvent = (transcriptEvent: Transcript) => {
      const results = transcriptEvent.results || []
      results.forEach((result: TranscriptResult) => {
        if (result.isPartial) return

        const alternatives = result.alternatives || []
        alternatives.forEach((alt: TranscriptAlternative) => {
          if (!alt.transcript) return

          const firstItem = alt.items?.[0]
          const speakerId =
            firstItem?.attendee?.externalUserId?.split("-")[1] || null
          if (speakerId !== userId) return

          const newText = alt.transcript.trim()
          if (!newText) return

          const timestamp = new Date(result.startTimeMs)
          const vocabularyFilterMatches = (alt.items || [])
            .filter(item => item.vocabularyFilterMatch)
            .map(item => item.content)
          const transcriptStale =
            bufferRef.current &&
            timestamp.getTime() - bufferRef.current.startTime.getTime() > 10000
          if (transcriptStale) flush() // flush if buffer spans more than 10s

          if (bufferRef.current) {
            bufferRef.current.text += " " + newText
            bufferRef.current.piiEntities.push(...(alt.entities || []))
            bufferRef.current.vocabularyFilterMatches.push(
              ...vocabularyFilterMatches
            )
          } else {
            bufferRef.current = {
              text: newText,
              speakerId: speakerId || "",
              startTime: timestamp,
              piiEntities: alt.entities || [],
              vocabularyFilterMatches,
            }
          }

          scheduleFlush()
        })
      })
    }

    audioVideo.transcriptionController.subscribeToTranscriptEvent(
      handleTranscriptEvent
    )

    return () => {
      audioVideo.transcriptionController.unsubscribeFromTranscriptEvent(
        handleTranscriptEvent
      )
      flush()
    }
  }, [enabled, audioVideo, bufferRef, channel, flush, userId, scheduleFlush])
}

export default useTranscriptForwarding
